import companyData from "const/companyData.json";

export const DEFAULT_COMPANY = companyData[0];

const uid = () => Math.random().toString(36).slice(2, 10);

export const isGst = (value) => value === "yes" || value === "true" || value === true;

export const companyHeader = (company) => ({
  companyName: company?.name ?? "",
  invoiceFromAbn: company?.abn ?? "",
  location: company?.location ?? "",
  email: company?.email ?? "",
  contact: company?.contact ?? "",
  logo: company?.logo ?? null,
  accountName: company?.paymentInfo?.accountName ?? "",
  accountNumber: company?.paymentInfo?.accountNumber ?? "",
  bankDetails: company?.paymentInfo?.bsb ?? "",
});

export const companyFor = (header) =>
  companyData.find((company) => company.name === header?.companyName) ?? null;

const toFormLine = (line) => ({
  key: uid(),
  id: line.id ?? null,
  chargeKey: line.chargeKey ?? null,
  description: line.description ?? "",
  quantity: String(line.quantity ?? 1),
  price: line.price == null ? "" : String(line.price),
  gst: isGst(line.gst) ? "yes" : "no",
});

export const newLine = () => toFormLine({});

export const formFromDraft = (draft, company = DEFAULT_COMPANY) => ({
  title: draft?.title ?? "",
  invoiceTo: draft?.invoiceTo ?? "",
  issueDate: draft?.issueDate ?? "",
  dueDate: draft?.dueDate ?? "",
  address: draft?.address ?? "",
  header: companyHeader(company),
  lines: (draft?.lines ?? []).map(toFormLine),
  chargesFingerprint: draft?.chargesFingerprint ?? null,
});

export const formFromInvoice = (invoice) => ({
  title: invoice?.title ?? "",
  invoiceTo: invoice?.invoiceTo ?? "",
  issueDate: invoice?.issueDate ?? "",
  dueDate: invoice?.dueDate ?? "",
  address: invoice?.address ?? "",
  header: {
    companyName: invoice?.companyName ?? "",
    invoiceFromAbn: invoice?.invoiceFromAbn ?? "",
    location: invoice?.location ?? "",
    email: invoice?.email ?? "",
    contact: invoice?.contact ?? "",
    logo: invoice?.logo ?? null,
    accountName: invoice?.paymentInfo?.accountName ?? "",
    accountNumber: invoice?.paymentInfo?.accountNumber ?? "",
    bankDetails: invoice?.paymentInfo?.bankDetails ?? "",
  },
  lines: (invoice?.invoices ?? []).map(toFormLine),
});

const lineAmount = (line) => (Number(line.price) || 0) * (Number(line.quantity) || 0);

export const formTotals = (lines = []) => {
  const subtotal = lines.reduce((sum, line) => sum + lineAmount(line), 0);
  const gst = lines
    .filter((line) => isGst(line.gst))
    .reduce((sum, line) => sum + lineAmount(line) * 0.1, 0);
  return { subtotal, gst, total: subtotal + gst };
};

const isWholeAtLeastOne = (value) => Number.isInteger(Number(value)) && Number(value) >= 1;

export const validateForm = (form) => {
  if (!form.title.trim()) return "Add a title.";
  if (!form.invoiceTo.trim()) return "Add who the invoice is to.";
  if (!form.issueDate || !form.dueDate) return "Set the issue and due dates.";
  if (form.dueDate < form.issueDate) return "The due date cannot be before the issue date.";
  if (form.lines.length === 0) return "Add at least one line.";
  if (form.lines.some((line) => !line.description.trim())) return "Every line needs a description.";
  if (form.lines.some((line) => !isWholeAtLeastOne(line.quantity)))
    return "Quantities must be whole numbers of at least 1.";
  if (form.lines.some((line) => line.price === "" || Number.isNaN(Number(line.price))))
    return "Every line needs a price.";
  return null;
};

export const toRequest = (form) => ({
  title: form.title.trim(),
  invoiceTo: form.invoiceTo.trim(),
  issueDate: form.issueDate,
  dueDate: form.dueDate,
  address: form.address?.trim() || null,
  ...form.header,
  lines: form.lines.map((line) => ({
    id: line.id,
    chargeKey: line.chargeKey,
    description: line.description.trim(),
    price: Number(line.price),
    quantity: Number(line.quantity),
    gst: isGst(line.gst) ? "yes" : "no",
  })),
  chargesFingerprint: form.chargesFingerprint ?? null,
});
